/**
 * Feedback Modal
 *
 * Simple form modal for user feedback.
 */

import { useState, useEffect, useRef } from 'react';
import { X } from '@phosphor-icons/react';
import { isValidEmail } from '../../lib/validation';
import { modalStyles, getCloseButtonStyle } from './styles';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface FormData {
  name: string;
  email: string;
  message: string;
}

interface ValidationErrors {
  message?: string;
  email?: string;
}

const initialFormData: FormData = {
  name: '',
  email: '',
  message: '',
};

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const styles = {
  ...modalStyles,
  modal: {
    ...modalStyles.modal,
    maxWidth: '480px',
  },
  textarea: {
    ...modalStyles.textarea,
    minHeight: '120px',
  },
};

export function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [closeHovered, setCloseHovered] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const isSubmittingRef = useRef(false);

  function validateForm(): ValidationErrors {
    const newErrors: ValidationErrors = {};

    if (!formData.message.trim()) {
      newErrors.message = 'Bericht is verplicht';
    }

    if (formData.email && !isValidEmail(formData.email.trim())) {
      newErrors.email = 'Voer een geldig e-mailadres in';
    }

    return newErrors;
  }

  function handleInputChange(field: keyof FormData, value: string): void {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof ValidationErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }

  function handleClose(): void {
    setFormData(initialFormData);
    setErrors({});
    setSubmitted(false);
    isSubmittingRef.current = false;
    onClose();
  }

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        handleClose();
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>): void {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();

    if (isSubmittingRef.current) {
      return;
    }

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim() || undefined,
          email: formData.email.trim() || undefined,
          message: formData.message.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Submission failed');
      }

      setSubmitted(true);
      isSubmittingRef.current = false;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setErrors({ message: 'Er is een fout opgetreden. Probeer het later opnieuw.' });
      isSubmittingRef.current = false;
    } finally {
      setIsSubmitting(false);
    }
  }

  if (!isOpen) return null;

  if (submitted) {
    return (
      <div style={styles.overlay} onClick={handleOverlayClick}>
        <div style={styles.modal} role="dialog" aria-modal="true" aria-labelledby="modal-title">
          <div style={styles.header}>
            <h2 id="modal-title" style={styles.title}>Bedankt!</h2>
            <button
              style={getCloseButtonStyle(closeHovered)}
              onClick={handleClose}
              aria-label="Sluiten"
              onMouseEnter={() => setCloseHovered(true)}
              onMouseLeave={() => setCloseHovered(false)}
            >
              <X size={20} weight="bold" />
            </button>
          </div>
          <div style={styles.content}>
            <p style={{ ...styles.description, marginBottom: 0 }}>
              Bedankt voor je feedback! We waarderen je input en zullen deze gebruiken
              om de Kaart van Mensenkennis te verbeteren.
            </p>
          </div>
          <div style={styles.footer}>
            <button style={styles.submitButton} onClick={handleClose}>
              Sluiten
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.modal} role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <div style={styles.header}>
          <h2 id="modal-title" style={styles.title}>Feedback</h2>
          <button
            style={getCloseButtonStyle(closeHovered)}
            onClick={handleClose}
            aria-label="Sluiten"
            onMouseEnter={() => setCloseHovered(true)}
            onMouseLeave={() => setCloseHovered(false)}
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <div style={styles.content}>
          <p style={styles.description}>
            Heb je suggesties, vragen of opmerkingen over de Kaart van Mensenkennis?
            We horen graag van je!
          </p>

          <form style={styles.form} onSubmit={handleSubmit}>
            <div style={styles.fieldGroup}>
              <label htmlFor="feedback-name" style={styles.label}>Naam</label>
              <input
                id="feedback-name"
                type="text"
                style={styles.input}
                placeholder="Uw naam (optioneel)"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label htmlFor="feedback-email" style={styles.label}>E-mail</label>
              <input
                id="feedback-email"
                type="email"
                style={{
                  ...styles.input,
                  ...(errors.email ? styles.inputError : {}),
                }}
                placeholder="uw@email.nl (optioneel)"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'email-error' : undefined}
              />
              {errors.email && (
                <span id="email-error" style={styles.errorText} role="alert">
                  {errors.email}
                </span>
              )}
            </div>

            <div style={styles.fieldGroup}>
              <label htmlFor="feedback-message" style={styles.label}>
                Bericht<span style={styles.required}>*</span>
              </label>
              <textarea
                id="feedback-message"
                style={{
                  ...styles.textarea,
                  ...(errors.message ? styles.inputError : {}),
                }}
                placeholder="Uw feedback..."
                value={formData.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                aria-invalid={!!errors.message}
                aria-describedby={errors.message ? 'message-error' : undefined}
              />
              {errors.message && (
                <span id="message-error" style={styles.errorText} role="alert">
                  {errors.message}
                </span>
              )}
            </div>
          </form>
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelButton} onClick={handleClose}>
            Annuleren
          </button>
          <button
            type="submit"
            style={{
              ...styles.submitButton,
              opacity: isSubmitting ? 0.7 : 1,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
            }}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Verzenden...' : 'Verzenden'}
          </button>
        </div>
      </div>
    </div>
  );
}
